// Module-level bus for the in-app IMAGE viewer, mirroring `xlsxPreview.ts`.
//
// Why an in-app viewer at all: `openDocument` used to send images to a bare browser tab,
// where the browser fits the image to the window and offers no zoom affordance — a
// photographed invoice was unreadable without downloading it. PDFs never had this problem
// because the browser's PDF plugin ships its own zoom. Jorge, 2026-09-18: "quando nós temos
// uma imagem e não um PDF, tem que aparecer aqui um zoom, uma lupa para zoom in, zoom out."
import { useSyncExternalStore } from 'react'

// `documentNumber` is the printed invoice reference from the extraction ("FT 2023A/0456") —
// shown beside the filename in the viewer's title when the document has one (Diogo, 2026-11-02).
export type ImagePreviewDoc = { id: string; filename: string; documentNumber?: string | null }

let current: ImagePreviewDoc | null = null
const listeners = new Set<() => void>()

// The families the signed preview endpoint can hand back something an <img> can decode.
// HEIC/HEIF and TIFF are included on purpose: `documents-sign-preview` signs
// `preview_path ?? storage_path`, and the poller persists a JPEG derivative for both, so the
// browser gets a JPEG even though the stored original stays HEIC/TIFF. Documents predating
// that backfill have no derivative — the viewer's onError falls back to opening the original.
export function isImageName(name?: string | null): boolean {
  return !!name && /\.(png|jpe?g|webp|gif|tiff?|heic|heif)$/i.test(name)
}

export function openImagePreview(doc: ImagePreviewDoc): void {
  current = doc
  listeners.forEach((l) => l())
}

export function closeImagePreview(): void {
  current = null
  listeners.forEach((l) => l())
}

/** Subscribe to the current preview target (null = closed). Used by the modal. */
export function useImagePreviewDoc(): ImagePreviewDoc | null {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb)
      return () => listeners.delete(cb)
    },
    () => current,
    () => current,
  )
}

// --- zoom model (pure, unit-tested) -------------------------------------------------
//
// Kept out of the component so the stepping rules are testable without a DOM. Zoom is a
// multiplier on the fit-to-container size: 1 = "fits the frame", 4 = four times that.
//
// Shared by the two surfaces that zoom: this module's full-screen viewer (reached from
// `openDocument`, where there is no pane to zoom inside) and `DocumentPreview`'s IN-PLACE
// controls (2026-09-29 — the preview pane zooms where it stands, no popup). One stepping rule,
// so 150% means the same thing in both.

export const MIN_ZOOM = 1
export const MAX_ZOOM = 8
const STEP = 1.5

export function zoomIn(z: number): number {
  return Math.min(MAX_ZOOM, round2(z * STEP))
}

export function zoomOut(z: number): number {
  return Math.max(MIN_ZOOM, round2(z / STEP))
}

/** Wheel/trackpad: negative deltaY is "towards the user" = zoom in, matching every map UI. */
export function zoomByWheel(z: number, deltaY: number): number {
  return deltaY < 0 ? zoomIn(z) : zoomOut(z)
}

function round2(n: number): number {
  return Math.round(n * 100) / 100
}

/** Percent shown in the toolbar. 1 → "100%". */
export function zoomLabel(z: number): string {
  return `${Math.round(z * 100)}%`
}
